import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { FormData, FormField, FormProviderService, PreventDoubleSubmission } from '@fry/lib/forms';
import { Role, RolesStore } from '@fry/lib/roles';
import { PermissionsService } from '@fry/lib/permissions';
import { RelationsStore } from '@fry/lib/relations';
import {
  SelectableListMapping,
  SelectableListMappingInput
} from '@fry/components/common/selectable-list/selectable-list.mapping';
import { pick } from '@fry/lib/utils';

@Injectable()
export class RoleForm {
    private formData: FormData;

    constructor(
        private provider: FormProviderService,
        private roles: RolesStore,
        private permissions: PermissionsService,
        private relations: RelationsStore
    ) { }

    _baseFields = [
        {
            id: 'title',
            title: $localize `Title`,
            initial: '',
            widget: {
                type: 'string',
                autocomplete: 'on'
            },
            validators: [Validators.required],
            order: 1,
        },
        {
            id: 'description',
            title: $localize `Description`,
            initial: '',
            widget: {
                type: 'string',
                autocomplete: 'on'
            },
            validators: [],
            order: 2,
        }];

    _otherFields = [
        {
            id: 'invitable',
            title: $localize `Allow new users to be invited to fill in next section. \
                    If selected then this role cannot be assigned any relations.`,
            initial: false,
            widget: {
                type: 'boolean'
            },
            validators: [],
            order: 6,
        },
        {
            id: 'isUserDependent',
            title: $localize `Mark this role as user dependent?`,
            initial: false,
            widget: {
                type: 'boolean'
            },
            validators: [],
            order: 8,
            showCondition: (_, parent) => {
                return !parent['invitable'];
            }
        },
        {
            id: 'contentRestriction',
            title: $localize `Would you like to restrict this access to only certain content or leave open to all?`,
            initial: false,
            widget: {
                type: 'select'
            },
            options: {
                categories: [
                    {'id': 'open', 'name': 'Open'},
                    {'id': 'restricted', 'name': 'Restricted'},
                ]
            },
            validators: [],
            order: 8,
            showCondition: (_, parent) => {
                return !parent['invitable'];
            }
        }
    ];

    getFields(role: Role): Observable<FormField[]> {
        return forkJoin(
            of(this._baseFields),
            this.getPermissions(),
            this.getRoles(role),
            this.getAssignableRoles(role),
            this.getRelations(),
            of(this._otherFields)
        ).pipe(
            map(res => {
                let flds = [].concat(...res);
                flds = flds.sort(((a, b) => a.order - b.order));
                return flds;
            })
        );
    }

    getPermissions() {
        return this.permissions.availableComponents.pipe(
            map((components: SelectableListMappingInput[]) => {
                return [{
                    id: 'permissions',
                    title: $localize `Select individual permissions for users with this role`,
                    initial: [],
                    options: {
                        categories: SelectableListMapping.create(components)
                    },
                    widget: { type: 'selectableList', autocomplete: '' },
                    validators: [],
                    order: 3,
                }];
            })
        );
    }

    getRelations() {
        return this.relations.allStub().pipe(
            map(relations => {
                return [{
                    id: 'relations',
                    title: $localize `Restrict the above permissions to be actionable only \
                        against other users of the system that match the following relations.`,
                    initial: [],
                    options: { categories: relations },
                    widget: { type: 'multiSelect', autocomplete: '' },
                    validators: [],
                    order: 7,
                    showCondition: (_, parent) => {
                        return !parent['invitable'];
                    }
                }];
            }));
    }

    getRoles(r: Role) {
        return this.roles.allStub().pipe(
            map(roles => {
                const opts = roles.filter(role => role.id !== r.id);
                return [{
                    id: 'appliesToRoles',
                    title: $localize `Restrict the above permissions to be actionable only against other users of the
                        system with the following roles. If left blank then the above permissions will be actionable \
                        against all users in the system.`,
                    initial: [],
                    options: { categories: opts },
                    widget: { type: 'multiSelect', autocomplete: '' },
                    validators: [],
                    order: 4,
                }];
            }));
    }

    getAssignableRoles(r: Role) {
        return this.roles.allStub().pipe(
            map(roles => {
                const opts = roles.filter(role => role.id !== r.id);
                return [{
                    id: 'assignableRoles',
                    title: $localize `Restrict the assigning of roles to other users to be from only those in the
                    following list.`,
                    initial: [],
                    options: { categories: opts },
                    widget: { type: 'multiSelect', autocomplete: '' },
                    validators: [],
                    order: 5,
                    showCondition: (_, parent) => {
                        return (parent['permissions'] || []).find(item => item === 'roles.assign');
                    }
                }];
            }));
    }

    init(role: Role): Observable<FormData> {
        return this.getFields(role).pipe(
            switchMap(fields => {
                return forkJoin(of(fields), this.prepareValue(fields, role));
            }),
            switchMap(([fields, data]) => this.provider.getFormDataWith(fields, data)),
            tap(formData => this.formData = formData)
        );
    }

    private prepareValue(fields: FormField[], role: Role) {
        return this.permissions.flatPermissionIDs.pipe(
            map(perms => {
                const ids = fields.map(item => item.id);
                const value = {...pick(role.doc, ids)};
                value['permissions'] = (value['permissions'] || []).filter(item => perms.indexOf(item) !== -1);
                return value;
            })
        );
    }

    private setValue(role, value) {
        return this.permissions.flatPermissionIDs.pipe(
            map(perms => {
                const unknownPermissions = (role.doc.permissions || []).filter(item => perms.indexOf(item) === -1);
                // We expect that only known permissions can be in value['permissions]
                value['permissions'] = [...unknownPermissions, ...value['permissions']];
                role.setValue(value);
                return role;
            })
        );
    }

    @PreventDoubleSubmission()
    submit(role: Role): Observable<Role> {
        if (this.formData.invalid) {
            return throwError($localize `Please review the form`);
        }

        return this.setValue(role, this.formData.getValue()).pipe(
            switchMap(obj => this.roles.save(obj))
        );
    }

    get pristine() {
        if (!this.formData) {
            return true;
        }
        return this.formData.pristine;
    }
}
